import { useCallback, useState } from "react";
import "@/App.css";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Grip } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { LiveTitle } from "./components/LiveTitle";
import { EmojiScale } from "./components/EmojiScale";
import { CommentBox } from "./components/CommentBox";
import { ThankYou } from "./components/ThankYou";
import { AudienceStep } from "./components/AudienceStep";
import { LockDial } from "./components/LockDial";
import { useKeyboardViewport } from "./hooks/useKeyboardViewport";
import { submitReview } from "./lib/api";

const Marquee = () => {
  const text = Array.from({ length: 8 }).map((_, i) => (
    <span key={i} className="mx-6">this place reviewed <span className="text-[var(--amber)]">✦</span> één moment, één gevoel <span className="text-[var(--amber)]">✦</span></span>
  ));
  return (
    <div data-testid="marquee" className="border-t border-[var(--line)] py-2 overflow-hidden text-xs uppercase tracking-[0.3em] text-[var(--muted)] whitespace-nowrap">
      <div className="marquee-track">{text}{text}</div>
    </div>
  );
};

const Logo = () => (
  <div data-testid="brand-logo" className="flex items-center gap-2">
    <img src="/favicon.svg" alt="" className="h-7 w-7" />
    <span className="font-display text-base">tpr<span className="text-[var(--amber)]">.</span></span>
  </div>
);

export default function App() {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [step, setStep] = useState("rate");
  const [audience, setAudience] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [locked, setLocked] = useState(false);
  const [focused, setFocused] = useState(false);
  const kb = useKeyboardViewport();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const compact = step === "rate" && Boolean(rating) && ((kb.open && focused) || (kb.height !== null && kb.height < 620));

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 80, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 80, damping: 20 });
  const sx = useTransform(mx, [-0.5, 0.5], ["-10%", "10%"]);

  const submit = async () => {
    if (!executeRecaptcha) {
      toast.error("De beveiligingscheck laadt nog. Probeer het zo opnieuw.");
      return;
    }
    setLoading(true);
    try {
      const token = await executeRecaptcha("submit_review");
      await submitReview(rating, comment, audience, token);
      setDone(true);
    } catch {
      toast.error("Je review kon niet worden opgeslagen. Probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  const reset = useCallback(() => { setDone(false); setRating(null); setComment(""); setAudience([]); setStep("rate"); }, []);
  const goAudience = () => { setFocused(false); document.activeElement?.blur(); setStep("audience"); };

  return (
    <div
      data-testid="review-page"
      onMouseMove={(e) => { mx.set(e.clientX / window.innerWidth - 0.5); my.set(e.clientY / window.innerHeight - 0.5); }}
      style={{ height: kb.height ? `${kb.height}px` : "100dvh" }}
      className="grain relative w-full overflow-clip flex flex-col bg-[var(--ink)]"
    >
      <motion.div className="spot -top-[30vmax] -right-[20vmax]" style={{ x: sx }} />
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-10 pt-4 shrink-0">
        <Logo />
        <motion.button
          data-testid="open-lock-modal-btn"
          onClick={() => setLocked(true)}
          whileHover={{ rotate: 90 }}
          whileTap={{ scale: 0.9 }}
          aria-label="Reviews bekijken"
          className="p-2.5 rounded-full border border-[var(--line)] hover:border-[var(--amber)] hover:text-[var(--amber)] transition-colors"
        >
          <Grip className="h-4 w-4" />
        </motion.button>
      </header>

      {step === "audience" ? (
        <main className="relative z-10 flex-1 min-h-0 flex flex-col px-5 sm:px-10 max-w-6xl w-full mx-auto">
          <AudienceStep selected={audience} setSelected={setAudience} onSubmit={submit} onBack={() => setStep("rate")} loading={loading} />
        </main>
      ) : (
      <main className={`relative z-10 flex-1 min-h-0 flex flex-col px-5 sm:px-10 max-w-6xl w-full mx-auto ${compact ? "justify-end gap-2 pb-2" : "justify-center gap-[3.5vh]"}`}>
        <motion.div style={compact ? {} : { rotateX: rx, rotateY: ry, transformPerspective: 900 }}>
          <LiveTitle compact={compact} />
        </motion.div>
        <EmojiScale value={rating} onChange={setRating} compact={compact} />
        <AnimatePresence>
          {rating && (
            <CommentBox key="comment" comment={comment} setComment={setComment} onSubmit={goAudience} onFocusChange={setFocused} compact={compact} />
          )}
        </AnimatePresence>
        {!rating && !compact && (
          <p data-testid="rating-hint" className="text-sm text-[var(--muted)]">Kies een gezichtje — daarna opent er een vak voor je opmerking.</p>
        )}
      </main>
      )}

      {!compact && step === "rate" && <Marquee />}

      <AnimatePresence>
        {done && <ThankYou key="ty" rating={rating} onAgain={reset} />}
        {locked && <LockDial key="lock" onClose={() => setLocked(false)} />}
      </AnimatePresence>
      <Toaster theme="dark" position="top-center" />
    </div>
  );
}
